import { useMutation } from '@tanstack/react-query';
import { Endpoint } from '@/constants';
import { postData } from '@/utils/postData';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { useCheckout } from '@/contexts/checkoutContext';

interface ThreeDSecureCompletionRequest {
  transactionId: string;
  paRes?: string;
  md?: string;
}

interface ThreeDSecureCompletionResponse {
  isSuccess: boolean;
  data?: {
    redirectUrl?: string;
    errorMessage?: string | null;
  };
  messages?: string[];
  orderNumber?: string;
}

const completeThreeDSecure = (data: ThreeDSecureCompletionRequest): Promise<ThreeDSecureCompletionResponse> =>
  postData(Endpoint.CreateQuickPayThreeDSecureOrder)(data as unknown as Record<string, unknown>);

interface UseThreeDSecureCompletionOptions {
  isBuyNow?: boolean;
}

export const useThreeDSecureCompletion = (options?: UseThreeDSecureCompletionOptions) => {
  const { isBuyNow = false } = options || {};
  
  // Conditionally access checkout context - only for regular checkout, not Buy Now
  const checkoutContext = !isBuyNow ? useCheckout() : null;
  const refreshCheckoutData = checkoutContext?.refreshCheckoutData;
  
  const { isPending, mutate } = useMutation<
    ThreeDSecureCompletionResponse,
    Error,
    ThreeDSecureCompletionRequest
  >({
    mutationFn: completeThreeDSecure,
    onError: error => {
      console.error(`Failed to complete 3D Secure authentication: ${error.message}`);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Payment authentication failed. Please try again.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    },
    onSuccess: async response => {
      console.log('3D Secure completion response received', response);
      
      // Handle successful order
      if (response.isSuccess && response.data?.redirectUrl) {
        console.log('3D Secure: Order completed successfully, redirecting to:', response.data.redirectUrl);
        
        // Refresh checkout data to clear the cart before redirecting (only if not Buy Now)
        if (!isBuyNow && refreshCheckoutData) {
          try {
            await refreshCheckoutData();
          } catch (error) {
            console.error('3D Secure: Failed to refresh checkout data after order', error);
          }
        }
        
        // Redirect to confirmation page
        window.location.href = response.data.redirectUrl;
        return;
      }
      
      // Handle successful order with order number (legacy response format)
      if (response.orderNumber) {
        // Build confirmation URL
        const confirmationUrl = `/checkout/order-confirmation?confirmationId=${response.orderNumber}`;
        
        // Refresh checkout data to clear the cart before redirecting (only if not Buy Now)
        if (!isBuyNow && refreshCheckoutData) {
          try {
            await refreshCheckoutData();
          } catch (error) {
            console.error('3D Secure: Failed to refresh checkout data after order', error);
          }
        }
        
        window.location.href = confirmationUrl;
        return;
      }
      
      // Handle error message in data
      if (response.data?.errorMessage) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.data.errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        return;
      }
      
      // Handle generic failure
      if (!response.isSuccess && response.messages && response.messages.length > 0) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.messages[0]}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        return;
      }
      
      // Unexpected response
      console.error('3D Secure: Unexpected response format', response);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              An unexpected error occurred. Please try again.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    },
  });

  return { isPending, completeThreeDSecure: mutate };
};